import {
  InspectionStatus,
  InspectionItemStatus,
  InspectionStatusEvent,
  AssignmentStatus,
} from '../enums/InspectionStatus';
import {
  INSPECTION_STATUS_MAP,
  INSPECTION_ITEM_STATUS_MAP,
  INSPECTION_EVENT_MAP,
  ASSIGNMENT_STATUS_MAP,
  TERMINAL_INSPECTION_STATUSES,
  ACTIVE_INSPECTION_STATUSES,
  AUTOMATIC_EVENTS,
  StatusInfo,
} from '../constants/statusMaps';

export function getInspectionStatusInfo(status: number): StatusInfo {
  return (
    INSPECTION_STATUS_MAP[status as InspectionStatus] || {
      label: 'Desconhecido',
      description: 'Status desconhecido',
      color: '#999999',
      badge: 'N/A',
    }
  );
}

export function getInspectionItemStatusInfo(status: number): StatusInfo {
  return (
    INSPECTION_ITEM_STATUS_MAP[status as InspectionItemStatus] || {
      label: 'Desconhecido',
      description: 'Status desconhecido',
      color: '#999999',
      badge: 'N/A',
    }
  );
}

export function getAssignmentStatusInfo(status: number): StatusInfo {
  return (
    ASSIGNMENT_STATUS_MAP[status as AssignmentStatus] || {
      label: 'Desconhecido',
      description: 'Status desconhecido',
      color: '#999999',
      badge: 'N/A',
    }
  );
}

export function getInspectionEventInfo(event: number): { label: string; description: string } {
  return (
    INSPECTION_EVENT_MAP[event as unknown as InspectionStatusEvent] || {
      label: 'Desconhecido',
      description: 'Evento desconhecido',
    }
  );
}

export function isTerminalStatus(status: number): boolean {
  return TERMINAL_INSPECTION_STATUSES.includes(status as InspectionItemStatus);
}

export function isActiveStatus(status: number): boolean {
  return ACTIVE_INSPECTION_STATUSES.includes(status as InspectionItemStatus);
}

export function isAutomaticEvent(event: number): boolean {
  return AUTOMATIC_EVENTS.includes(event as unknown as InspectionStatusEvent);
}

export function formatInspectionStatus(status: number): string {
  const info = getInspectionStatusInfo(status);
  return `${status} - ${info.label}`;
}

export function formatInspectionItemStatus(status: number): string {
  const info = getInspectionItemStatusInfo(status);
  return `${status} - ${info.label}`;
}

export function getStatusColor(status: number): string {
  return getInspectionStatusInfo(status).color;
}

export function getItemStatusColor(status: number): string {
  return getInspectionItemStatusInfo(status).color;
}

export function getStatusBadge(status: number): string {
  return getInspectionStatusInfo(status).badge;
}

export function getItemStatusBadge(status: number): string {
  return getInspectionItemStatusInfo(status).badge;
}

export function canCancelInspection(status: number): boolean {
  return !isTerminalStatus(status);
}

export function canScheduleInspection(status: number): boolean {
  return status === InspectionItemStatus.AguardandoAgendamento;
}

export function canRescheduleInspection(status: number): boolean {
  return status === InspectionItemStatus.Agendado;
}

export function canCheckIn(status: number): boolean {
  return status === InspectionItemStatus.Agendado;
}

export function canFinalizeItem(itemStatus: number): boolean {
  return itemStatus === InspectionItemStatus.CheckinRealizado;
}

export function canAssignInspection(status: number): boolean {
  return (
    status === InspectionItemStatus.AguardandoDirecionamento ||
    status === InspectionItemStatus.AguardandoAceitePrestadora ||
    status === InspectionItemStatus.PrestadoraRecusou ||
    status === InspectionItemStatus.AguardandoDirecionamentoInspetor ||
    status === InspectionItemStatus.InspetorRecusou
  );
}

export function getNextAction(status: number): string | null {
  switch (status) {
    case InspectionItemStatus.AguardandoDirecionamento:
      return 'Direcionar para prestadora';
    case InspectionItemStatus.AguardandoAceitePrestadora:
      return 'Aguardar aceite da prestadora';
    case InspectionItemStatus.PrestadoraRecusou:
      return 'Direcionar para outra prestadora';
    case InspectionItemStatus.AguardandoDirecionamentoInspetor:
      return 'Direcionar para inspetor';
    case InspectionItemStatus.AguardandoAceiteInspetor:
      return 'Aguardar aceite do inspetor';
    case InspectionItemStatus.InspetorRecusou:
      return 'Direcionar para outro inspetor';
    case InspectionItemStatus.AguardandoAgendamento:
      return 'Agendar inspeção';
    case InspectionItemStatus.Agendado:
      return 'Realizar check-in no local';
    case InspectionItemStatus.CheckinRealizado:
      return 'Preencher checklist e finalizar';
    case InspectionItemStatus.AguardandoGeracaoLaudo:
      return 'Transmitir laudo para revisão';
    case InspectionItemStatus.Finalizado:
      return null;
    case InspectionItemStatus.Cancelado:
    case InspectionItemStatus.Frustrado:
      return null;
  }
  
  switch (status) {
    case InspectionStatus.Solicitacao:
    case InspectionStatus.AguardandoDirecionamento:
      return 'Direcionar para prestadora';
    case InspectionStatus.AguardandoAceitePrestadora:
      return 'Aguardar aceite da prestadora';
    case InspectionStatus.AguardandoAceiteInspetor:
      return 'Aguardar aceite do inspetor';
    case InspectionStatus.AceitaInspetor:
      return 'Agendar inspeção';
    case InspectionStatus.Agendada:
      return 'Realizar check-in no local';
    case InspectionStatus.EmExecucao:
      return 'Finalizar todos os itens';
    case InspectionStatus.AguardandoEnvioDados:
      return 'Enviar dados coletados';
    case InspectionStatus.AguardandoRevisaoInspecao:
      return 'Revisar e aprovar';
    case InspectionStatus.Aprovada:
      return 'Concluir inspeção';
    case InspectionStatus.DevolvidaParaAjustes:
      return 'Fazer ajustes necessários';
    default:
      return null;
  }
}

export function getStatusProgress(status: number): number {
  const progressMap: Record<number, number> = {
    [InspectionItemStatus.AguardandoDirecionamento]: 5,
    [InspectionItemStatus.AguardandoAceitePrestadora]: 15,
    [InspectionItemStatus.PrestadoraRecusou]: 0,
    [InspectionItemStatus.AguardandoDirecionamentoInspetor]: 25,
    [InspectionItemStatus.AguardandoAceiteInspetor]: 35,
    [InspectionItemStatus.InspetorRecusou]: 0,
    [InspectionItemStatus.AguardandoAgendamento]: 45,
    [InspectionItemStatus.Agendado]: 60,
    [InspectionItemStatus.CheckinRealizado]: 75,
    [InspectionItemStatus.AguardandoGeracaoLaudo]: 85,
    [InspectionItemStatus.Finalizado]: 100,
    [InspectionItemStatus.Cancelado]: 0,
    [InspectionItemStatus.Frustrado]: 0,
  };

  return progressMap[status] || 0;
}

export function calculateItemsProgress(items: Array<{ status: number }>): {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  percentage: number;
} {
  const total = items.length;
  const completed = items.filter((item) => item.status === InspectionItemStatus.Finalizado).length;
  const inProgress = items.filter(
    (item) => item.status === InspectionItemStatus.CheckinRealizado ||
              item.status === InspectionItemStatus.Agendado ||
              item.status === InspectionItemStatus.AguardandoGeracaoLaudo
  ).length;
  const pending = items.filter(
    (item) => item.status === InspectionItemStatus.AguardandoDirecionamento ||
              item.status === InspectionItemStatus.AguardandoAceitePrestadora ||
              item.status === InspectionItemStatus.AguardandoDirecionamentoInspetor ||
              item.status === InspectionItemStatus.AguardandoAceiteInspetor ||
              item.status === InspectionItemStatus.AguardandoAgendamento
  ).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    total,
    completed,
    inProgress,
    pending,
    percentage,
  };
}

export function areAllItemsFinalized(items: Array<{ status: number }>): boolean {
  return items.every((item) => item.status === InspectionItemStatus.Finalizado);
}

export function hasItemInProgress(items: Array<{ status: number }>): boolean {
  return items.some((item) => item.status === InspectionItemStatus.CheckinRealizado);
}

export function getStatusByCategory(): {
  new: InspectionItemStatus[];
  pending: InspectionItemStatus[];
  active: InspectionItemStatus[];
  completed: InspectionItemStatus[];
  cancelled: InspectionItemStatus[];
} {
  return {
    new: [InspectionItemStatus.AguardandoDirecionamento],
    pending: [
      InspectionItemStatus.AguardandoAceitePrestadora,
      InspectionItemStatus.AguardandoDirecionamentoInspetor,
      InspectionItemStatus.AguardandoAceiteInspetor,
      InspectionItemStatus.AguardandoAgendamento,
    ],
    active: [
      InspectionItemStatus.Agendado,
      InspectionItemStatus.CheckinRealizado,
      InspectionItemStatus.AguardandoGeracaoLaudo,
    ],
    completed: [InspectionItemStatus.Finalizado],
    cancelled: [
      InspectionItemStatus.Cancelado,
      InspectionItemStatus.PrestadoraRecusou,
      InspectionItemStatus.InspetorRecusou,
      InspectionItemStatus.Frustrado,
    ],
  };
}
